import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadBulkProvisioningFile } from '../BulkProvisioningApi/bulkProvisioningApi.ts';
import { useState } from 'react';
import { AuthError } from 'auth/utils.ts';
import { useTranslation } from 'react-i18next';
import { provisioningQueries } from '@/activityLog/bulkProvisioning/utils/queries.ts';
import {
  GoogleTagManagerActions,
  sendGoogleTagManagerAction,
} from 'common/utils/sendGoogleTagManagerAction.ts';

export type Error = {
  title: string;
  description: string[];
};

export const useBulkProvisioningUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const queryClient = useQueryClient();

  const [importError, setImportError] = useState<Error | null>(null);

  const { t } = useTranslation();

  const isMutating = useIsMutating({ mutationKey: provisioningQueries.upload });
  const { mutate: uploadFileMutation } = useMutation({
    mutationFn: (file: File) => {
      return uploadBulkProvisioningFile(file);
    },
    mutationKey: provisioningQueries.upload,
    onSuccess: () => {
      setFile(null);
      queryClient.invalidateQueries({ queryKey: provisioningQueries.all });
      sendGoogleTagManagerAction(GoogleTagManagerActions.UploadFileSuccess);
    },
    onError: (error) => {
      if (error instanceof AuthError) {
        throw error;
      } else {
        const parsedError = JSON.parse(error.message);
        if (parsedError?.validationErrors) {
          const messages: string[] = [];
          for (const key in parsedError.validationErrors) {
            messages.push(parsedError.validationErrors[key][0]?.message);
          }
          if (messages.length > 0) {
            return setImportError({
              title: t('bulkProvisioning.bulkProvisioningFailed'),
              description: messages,
            });
          }
        }

        setImportError({
          title: t('bulkProvisioning.bulkProvisioningFailed'),
          description: [t('common.somethingWrong')],
        });
      }

      sendGoogleTagManagerAction(GoogleTagManagerActions.UploadFileFailure);
    },
  });

  const uploadFile = () => {
    if (file) {
      uploadFileMutation(file);
    }
  };

  return {
    selectedFile: file,
    selectFile: setFile,
    uploadFile,
    importError,
    setImportError,
    isPending: !!isMutating,
  };
};
